import { Alert, List, Loader, Stack, Text } from '@mantine/core';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { CapTableRail } from '@dealstack/shared/lib/generated';
import type { RedemptionCounterpartPreview } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useRedemptionCounterpartPreview.types.ts';

/**
 * CAP-2039: the other side of an inseparable redemption, shown in the form rather than after Save.
 *
 * The counterpart is READ-ONLY on purpose. In a nominee structure it is a fact of the legal
 * structure, not a choice: if an investor redeems their beneficial interest, the nominee's legal leg
 * IS the other side. Offering an override would let an admin book a register that misstates legal
 * title — a compliance problem rather than a UX one — and it would contradict both the rail-balance
 * invariant and the field freeze on a written group (120250). If the derivation looks wrong to the
 * admin, the defect is upstream in the holdings data, and showing the derivation plainly is how that
 * becomes visible.
 */
interface Props {
	preview: RedemptionCounterpartPreview;
	interest: CapTableRail;
	securityName: string;
}

const RAIL_KEY = {
	[CapTableRail.BENEFICIAL]: 'transactions.interest.legal',
	[CapTableRail.LEGAL]: 'transactions.interest.beneficial',
};

export const RedemptionCounterpartPanel = ({ preview, interest, securityName }: Props) => {
	const tCapManage = useTranslationCapManage();

	if (preview.status === 'idle') {
		return null;
	}

	if (preview.status === 'loading') {
		return (
			<Alert
				variant="light"
				color="gray"
				data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_REDEMPTION_COUNTERPART_PANEL}
			>
				<Stack gap={4}>
					<Loader size="xs" />
					<Text size="sm" c="dimmed">
						{tCapManage('transactions.redemption_counterpart.resolving', {
							defaultValue:
								'Working out which interest is retired alongside this one...',
						})}
					</Text>
				</Stack>
			</Alert>
		);
	}

	// A refusal here is information about the register, not a rejection of the user — they have not
	// asked for anything yet. Amber rather than red, and the resolver's own sentence, which already
	// names the route forward.
	if (preview.status === 'refused') {
		return (
			<Alert
				variant="light"
				color="orange"
				title={tCapManage('transactions.redemption_counterpart.cannot_resolve', {
					defaultValue: 'This cannot be retired as entered',
				})}
				data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_REDEMPTION_COUNTERPART_PANEL}
			>
				<Text size="sm">
					{preview.message ??
						tCapManage('transactions.redemption_counterpart.cannot_resolve_generic', {
							defaultValue:
								'The matching interest over these shares could not be identified.',
						})}
				</Text>
			</Alert>
		);
	}

	const counterpartRail = tCapManage(RAIL_KEY[interest]).toLowerCase();

	return (
		<Alert
			variant="light"
			color="blue"
			title={tCapManage('transactions.redemption_counterpart.title', {
				defaultValue: 'Retired alongside this',
			})}
			data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_REDEMPTION_COUNTERPART_PANEL}
		>
			<Stack gap={4}>
				<Text size="sm">
					{tCapManage('transactions.redemption_counterpart.intro', {
						counterpartRail,
						defaultValue:
							'These shares carry a {{counterpartRail}} interest held by someone else. Retiring them retires that interest too, over the same shares:',
					})}
				</Text>
				<List size="sm" withPadding>
					{preview.legs.map((leg) => (
						<List.Item key={`${leg.counterpartHolderId}-${leg.start}-${leg.end}`}>
							{tCapManage('transactions.redemption_counterpart.leg', {
								holder: leg.counterpartHolderName,
								units: leg.units,
								security: securityName,
								range: `[${leg.start}-${leg.end}]`,
								defaultValue: '{{holder}}: {{units}} {{security}} {{range}}',
							})}
						</List.Item>
					))}
				</List>
				{preview.spansMultiplePositions ? (
					<Text size="sm" c="orange.7">
						{tCapManage('transactions.redemption_counterpart.multi_position', {
							defaultValue:
								"This reaches across more than one of the holder's positions.",
						})}
					</Text>
				) : null}
			</Stack>
		</Alert>
	);
};
